import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectModalName } from 'redux/modal/ModalSlice';
import { selectSelectedWorkflow } from 'redux/runOrchestrations/RunOrchestrationsSlice';
import { MODAL_NAMES } from 'components/Modals/modal-list';
import { IWorkflowProduct } from 'interfaces/runOrchestration/workflowRow';
import { addToast } from 'redux/toast/toastSlice';
import { MAX_INTEGER_CSB } from 'utils/common-constants';
import { parameters } from './consts';
import { ICreateProductParametersProperties } from './types';

type FormHelpers = Pick<
  ICreateProductParametersProperties,
  'clearErrors' | 'errors' | 'setError' | 'setValue'
>;

export function useCreateProductParameters({
  clearErrors,
  errors,
  setError,
  setValue,
}: FormHelpers) {
  const dispatch = useAppDispatch();
  const modalName = useAppSelector(selectModalName);
  const selectedOrchestration = useAppSelector(selectSelectedWorkflow);

  useEffect(() => {
    if (modalName === MODAL_NAMES.CREATE_PRODUCT) {
      setError('name', { message: 'This field is required', type: 'custom' });
    }
    if (modalName === MODAL_NAMES.EDIT_PRODUCT && selectedOrchestration) {
      for (const [key, value] of Object.entries(selectedOrchestration)) {
        if (value) {
          setValue(key as keyof IWorkflowProduct, value);
        }
      }
    }
  }, [modalName, selectedOrchestration, setError, setValue]);

  const validateName = useCallback(
    (value: string) => {
      const hasNameError = !!errors.name;
      if (!value && !hasNameError) {
        setError('name', { message: 'This field is required', type: 'custom' });
      } else if (value && hasNameError) {
        clearErrors('name');
      }
    },
    [clearErrors, errors.name, setError]
  );

  const validateThrottleRate = useCallback(
    (value: string) => {
      const hasThrottleError = !!errors.throttleRate;
      const parsedValue = Number.parseInt(value);
      const isValidThrottleRate =
        value === 'null' ||
        value === '' ||
        (!Number.isNaN(+value) &&
          parsedValue >= 0 &&
          parsedValue <= MAX_INTEGER_CSB);

      if (isValidThrottleRate && parsedValue === 0) {
        dispatch(
          addToast({
            additionalData: {
              text: 'All orchestration instances for a product variant with a "0" throttle rate value will be marked as "Pending"',
            },
            closeTimeout: 7000,
            id: `throtttleRate-${(Math.random() + 1).toString()}`,
            status: 'info',
            type: 'informationToast',
          })
        );
      }

      if (isValidThrottleRate && hasThrottleError) {
        clearErrors('throttleRate');
      } else if (!isValidThrottleRate && !hasThrottleError) {
        setError('throttleRate', {
          message: 'Use only positive numbers or null',
          type: 'custom',
        });
      }
    },
    [clearErrors, dispatch, errors.throttleRate, setError]
  );

  const validateMaxLength = useCallback(
    (id: string, value: string, maxLength: number) => {
      const hasError = !!errors[id];
      const exceedsMaxLength = value.length > maxLength;
      if (exceedsMaxLength && !hasError) {
        setError(id, {
          message: `Maximum length is ${maxLength} characters`,
          type: 'custom',
        });
      } else if (!exceedsMaxLength && hasError) {
        clearErrors(id);
      }
    },
    [clearErrors, errors, setError]
  );

  const handleFieldChange = useCallback(
    (id: string, value: string) => {
      setValue(id, value);

      if (id === 'name') {
        validateName(value);
      } else if (id === 'throttleRate') {
        validateThrottleRate(value);
      }

      const parameter = parameters.find((p) => p.paramId === id);
      if (parameter?.maxLength) {
        validateMaxLength(id, value, parameter.maxLength);
      }
    },
    [setValue, validateName, validateThrottleRate, validateMaxLength]
  );

  return { handleFieldChange, modalName, selectedOrchestration };
}
